import { Component, OnInit } from '@angular/core';
import { UserService, PageableUsersData } from '../../services/user.service';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { User } from '../../types/user-type.type';
import { UserRoleType } from '../../types/user-role.type';
import { BasicUser } from '../../services/user.service';

interface UserUpdateRequest {
  id: number;
  email?: string;
  cpf?: string;
  name: string;
  shortName: string;
  role: string;
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardModule,
    TableModule,
    PaginatorModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToastModule,
  ],
  template: `
    <p-toast></p-toast>
    <p-card>
      <ng-template pTemplate="header">
        <h3>Gerenciamento de Usuários</h3>
      </ng-template>
        <div *ngIf="!selectedUser" class="selected-user">
          <form [formGroup]="filterForm" (ngSubmit)="searchRegisters()">
            <div class="filter-inputs">
              <div>
                <label>Usuário:</label>
                <p-select 
                  formControlName="userId" 
                  [options]="allUsersOptions" 
                  optionLabel="label" 
                  optionValue="value"
                  placeholder="Selecione um usuário"
                  styleClass="w-full">
                </p-select>
              </div>

              <div>
                <label>Acesso:</label>
                <p-select 
                  formControlName="role" 
                  [options]="roleOptions" 
                  optionLabel="label" 
                  optionValue="value"
                  placeholder="Selecione um acesso"
                  styleClass="w-full">
                </p-select>
              </div>

              <div class="button-group">
                <p-button 
                  label="Buscar" 
                  type="submit"
                  styleClass="p-button-primary">
                </p-button>
                <p-button 
                  label="Limpar" 
                  type="button"
                  (onClick)="clearFilters()"
                  styleClass="p-button-secondary">
                </p-button>
              </div>
            </div>
          </form>
        </div>

        <div *ngIf="selectedUser" class="selected-user">
          <h6>Usuário Selecionado</h6>
          <form [formGroup]="userForm" (ngSubmit)="setUser()">
            <div class="field">
              <label>Nome Curto:</label>
              <input 
                pInputText
                formControlName="shortName"
                readonly
                styleClass="w-full" />
            </div>

            <div class="field">
              <label>Nome Completo:</label>
              <input 
                pInputText
                formControlName="name" 
                readonly
                styleClass="w-full" />
            </div>

            <div class="field">
              <label>CPF:</label>
              <input 
                pInputText
                formControlName="cpf" 
                readonly
                styleClass="w-full" />
            </div>

            <div class="field">
              <label>Email:</label>
              <input 
                pInputText
                formControlName="email" 
                readonly
                styleClass="w-full" />
            </div>

            <div class="field">
              <label>Acesso:</label>
              <p-select 
                formControlName="role" 
                [options]="rolesOptions" 
                optionLabel="label" 
                optionValue="value"
                styleClass="w-full">
              </p-select>
            </div>

            <div class="button-group">
              <p-button 
                label="Voltar"
                (onClick)="clearSelection()"
                styleClass="p-button-secondary">
              </p-button>
              <p-button 
                label="Alterar" 
                type="submit"
                styleClass="p-button-primary">
              </p-button>
            </div>
          </form>
        </div>

        <div class="table-container" *ngIf="!selectedUser">
          <p-table [value]="users">
            <ng-template pTemplate="header">
              <tr>
                <th>Nome Curto</th>
                <th>Nome Completo</th>
                <th>CPF</th>
                <th>Email</th>
                <th>Acesso</th>
                <th>Ações</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-user>
              <tr>
                <td>{{ user.shortName }}</td>
                <td>{{ user.name }}</td>
                <td>{{ user.cpf }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.role }}</td>
                <td>
                  <p-button 
                    icon="pi pi-pencil" 
                    (onClick)="selectUser(user)"
                    styleClass="p-button-sm p-button-text">
                  </p-button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <p-paginator
          *ngIf="!selectedUser"
          [totalRecords]="totalUsers"
          [rows]="pageSize"
          [first]="currentPage * pageSize"
          [rowsPerPageOptions]="[5, 10, 25, 100]"
          (onPageChange)="onPageChange($event)">
        </p-paginator>
    </p-card>
  `,
  styles: [
    `
      .filter-inputs {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
        align-items: end;
        margin-bottom: 20px;
      }

      .filter-inputs > div {
        display: flex;
        flex-direction: column;
      }

      .filter-inputs label {
        margin-bottom: 5px;
        font-weight: 500;
      }

      .button-group {
        display: flex;
        gap: 0.5rem;
        align-self: flex-end;
      }

      .selected-user {
        background: #f8f9fa;
        padding: 20px;
        border-radius: 6px;
        margin-bottom: 20px;
        border: 1px solid #dee2e6;
      }

      .field {
        margin-bottom: 1rem;
      }

      .field label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
        color: #495057;
      }

      .w-full {
        width: 100%;
      }

      .table-container {
        overflow-x: auto;
        margin-top: 20px;
      }

      @media (max-width: 768px) {
        .filter-inputs {
          grid-template-columns: 1fr;
        }

        .button-group {
          flex-direction: column;
          width: 100%;
        }

        .button-group p-button {
          width: 100%;
          margin: 5px 0;
        }
      }

      @media (min-width: 769px) {
        .table-container {
          overflow-x: visible;
        }
      }

      :host ::ng-deep .p-card .p-card-body {
        padding: 2rem;
      }

      :host ::ng-deep .p-button {
        margin-right: 0.5rem;
      }
    `,
  ],
})
export class UserComponent implements OnInit {
  users: User[] = [];
  roles: { id: number; name: string; description: string }[] = [];
  selectedUser: User | null = null;
  totalUsers: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  userForm: FormGroup;
  allUsers: BasicUser[] = [];
  allUsersOptions: { label: string; value: number }[] = [];
  rolesOptions: { label: string; value: string }[] = [];

  filterForm: FormGroup;

  roleOptions = [
    { value: '', label: '' },
    ...Object.keys(UserRoleType).map((key) => ({
      value: key,
      label: UserRoleType[key as keyof typeof UserRoleType],
    })),
  ];

  constructor(
    private userService: UserService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.userForm = this.fb.group({
      shortName: ['', Validators.required],
      name: ['', Validators.required],
      cpf: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['', Validators.required],
    });

    this.filterForm = this.fb.group({
      userId: [''],
      role: [''],
    });
  }

  ngOnInit(): void {
    this.loadAllUsers();
    this.loadRoles();
    this.searchRegisters();
  }

  loadAllUsers(): void {
    this.userService.getUsers().subscribe({
      next: (response) => {
        this.allUsers = response.data.sort((a, b) =>
          a.shortName.localeCompare(b.shortName)
        );
        this.allUsersOptions = [
          { label: '', value: 0 },
          ...this.allUsers.map(user => ({
            label: user.shortName,
            value: user.id
          }))
        ];
      },
      error: (error) => console.error('Error loading users:', error),
    });
  }

  loadRoles() {
    this.userService.getRoles().subscribe({
      next: (response) => {
        if (response?.data) {
          this.roles = response.data.sort((a, b) =>
            a.description.localeCompare(b.description)
          );
          this.rolesOptions = this.roles.map(role => ({
            label: `${role.name} | ${role.description}`,
            value: role.name
          }));
        }
      },
      error: (error) => console.error('Error loading roles:', error),
    });
  }

  searchRegisters() {
    const { userId, role } = this.filterForm.value;
    this.userService
      .getUsersByFilter(
        userId ? Number(userId) : undefined,
        role ? role : undefined,
        this.currentPage
      )
      .subscribe({
        next: (data) => {
          this.users = data.content.sort((a, b) =>
            a.shortName.localeCompare(b.shortName)
          );
          this.totalUsers = data.totalElements;
        },
        error: (error) => this.handleError('Erro na busca', error),
      });
  }

  selectUser(user: User): void {
    this.userService.getUserById(user.id).subscribe({
      next: (user) => {
        this.selectedUser = user;
        this.userForm.patchValue({
          shortName: user.shortName || '',
          name: user.name || '',
          cpf: user.cpf || '',
          email: user.email || '',
          role: user.role || '',
        });
      },
      error: (error) => this.handleError('Erro ao carregar usuário', error),
    });
  }

  setUser() {
    if (this.userForm.invalid || !this.selectedUser) return;

    this.userService
      .setUserById(this.selectedUser.id, this.userForm.value)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Usuário atualizado!'
          });
          this.clearSelection();
          this.searchRegisters();
        },
        error: (error) => this.handleError('Erro ao atualizar', error),
      });
  }

  clearFilters() {
    this.filterForm.reset();
    this.searchRegisters();
  }

  clearSelection(): void {
    this.selectedUser = null;
  }

  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.searchRegisters();
  }

  private handleError(message: string, error: any) {
    console.error(message, error);
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail: message
    });
  }
}
